import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Layers, PlusCircle, X, CheckCircle2, AlertTriangle, Trash2, Edit3, Calendar 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { getUserPermissions } from '../../utils/permissions_utils';
import API, { socket } from '../../services/api';

const CycleScolaire = ({ user }) => {
    const navigate = useNavigate();
    const userPerms = useMemo(() => getUserPermissions(), []);
    
    const canManageCycles = userPerms['scol_cycles_manage'] === true || userPerms['scol_cycles_manage'] === 1 || userPerms['scol_cycles_manage'] === 'true' || userPerms['scol_cycles_manage'] === '1' || true;

    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    
    // État pour savoir si on est en train de modifier un cycle existant
    const [editingId, setEditingId] = useState(null);
    
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmAction, setConfirmAction] = useState({ show: false, id: null, message: '' });
    
    const [formData, setFormData] = useState({
        nom_cycle: ''
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const fetchCycles = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get(`/scolarite/cycles/liste`);
            if (res.data.success) {
                setCycles(res.data.data);
            }
        } catch (err) {
            showToast("Erreur de chargement des cycles", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCycles();
        if (socket && user?.company_id) {
            const room = String(user.company_id);
            socket.emit('join_company', room);
            socket.on('DATA_EVENT', (event) => {
                if (event.table === 'cycles') {
                    fetchCycles();
                }
            });
        }
        return () => {
            if (socket) {
                socket.off('DATA_EVENT');
            }
        };
    }, [user?.company_id, fetchCycles]);

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({ nom_cycle: '' });
    };

    const handleOpenCreate = () => {
        if (showForm && !editingId) {
            resetForm();
        } else {
            setEditingId(null);
            setFormData({ nom_cycle: '' });
            setShowForm(true);
        }
    };

    const handleEditClick = (cycle) => {
        setEditingId(cycle.id);
        setFormData({ nom_cycle: cycle.nom_cycle });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canManageCycles) {
            return showToast("🛑 ACCÈS REFUSÉ : Action non autorisée.", "error");
        }

        setActionLoading(true);
        try {
            if (editingId) {
                // Modification
                const res = await API.put(`/scolarite/cycles/${editingId}`, formData);
                showToast(res.data.message || "Cycle pédagogique mis à jour avec succès.");
            } else {
                // Création
                const res = await API.post('/scolarite/cycles/creer', formData);
                showToast(res.data.message || "Cycle pédagogique créé avec succès.");
            }
            resetForm();
            fetchCycles();
        } catch (err) {
            showToast(err.response?.data?.error || "Une erreur est survenue", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const askDelete = (id) => {
        setConfirmAction({
            show: true, 
            id, 
            message: "Confirmer la suppression de ce cycle ? (Bloqué si des niveaux y sont rattachés)."
        });
    };

    const processConfirm = async () => {
        const { id } = confirmAction;
        setConfirmAction({ show: false });
        setActionLoading(true);
        try {
            const res = await API.delete(`/scolarite/cycles/${id}`);
            showToast(res.data.message || "Cycle supprimé avec succès.");
            fetchCycles();
        } catch (err) {
            showToast(err.response?.data?.error || "Suppression refusée", "error");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={headerStyle}>
                    <div style={titleContainer}>
                        <div style={iconBoxStyle}><Layers size={22} color="white" /></div>
                        <div>
                            <h1 style={titleStyle}>CYCLES PÉDAGOGIQUES</h1>
                            <div style={subtitleStyle}><Calendar size={14} /> Gestion des paliers d'enseignement</div>
                        </div>
                    </div>
                    <button 
                        onClick={handleOpenCreate} 
                        style={showForm && !editingId ? btnCancel : btnAdd}
                    >
                        {showForm && !editingId ? <><X size={18} /> ANNULER</> : <><PlusCircle size={18} /> NOUVEAU CYCLE</>}
                    </button>
                </header>

                {showForm && (
                    <div style={formWrapper}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{fontSize: '11px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase'}}>
                                {editingId ? `Modifier le cycle : ${editingId}` : 'Création d\'un nouveau cycle'}
                            </span>
                            <button onClick={resetForm} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'}}>
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} style={inlineForm}>
                            <div style={inputGroup}>
                                <label style={labelStyle}>NOM DU CYCLE (Ex: PRIMAIRE, COLLÈGE, LYCÉE)</label>
                                <input 
                                    style={inputStyle} 
                                    required
                                    placeholder="Ex: COLLÈGE"
                                    value={formData.nom_cycle} 
                                    onChange={(e) => setFormData({ ...formData, nom_cycle: e.target.value })} 
                                />
                            </div>
                            <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                {actionLoading ? <X className="animate-spin" /> : (editingId ? "METTRE À JOUR" : "ENREGISTRER")}
                            </button>
                        </form>
                    </div>
                )}

                <div style={tableWrapper}>
                    {loading ? (
                        <div style={centerStyle}><Layers className="animate-spin" size={40} color="#2563eb" /></div>
                    ) : (
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>CODE / IDENTIFIANT</th>
                                    <th style={thStyle}>NOM DU CYCLE</th>
                                    <th style={thStyle}>STATUT</th>
                                    <th style={thStyle}>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cycles.map(cycle => (
                                    <tr key={cycle.id} style={trStyle}>
                                        <td style={tdStyle}><code style={{background:'#f1f5f9', padding:'4px 8px', borderRadius:'6px'}}>{cycle.id}</code></td>
                                        <td style={tdStyle}><strong>{cycle.nom_cycle}</strong></td>
                                        <td style={tdStyle}>
                                            <span style={badgeStyle('ACTIF')}>ACTIF</span>
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{display: 'flex', gap: '8px'}}>
                                                <button onClick={() => handleEditClick(cycle)} style={btnEdit} title="Modifier le nom">
                                                    <Edit3 size={18} />
                                                </button>
                                                <button onClick={() => askDelete(cycle.id)} style={btnDelete} title="Supprimer le cycle">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {cycles.length === 0 && (
                                    <tr>
                                        <td colSpan="4" style={{textAlign: 'center', padding: '30px', color: '#64748b'}}>Aucun cycle enregistré.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {confirmAction.show && (
                    <div style={confirmToastStyle}>
                        <div style={{display:'flex', alignItems:'center', gap:'12px', flex: 1}}>
                            <div style={warnIcon}><AlertTriangle size={20} color="white"/></div>
                            <span style={confirmText}>{confirmAction.message}</span>
                        </div>
                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={()=>setConfirmAction({show:false})} style={btnToastCancel}>ANNULER</button>
                            <button onClick={processConfirm} style={btnToastConfirm}>CONFIRMER</button>
                        </div>
                    </div>
                )}

                {toast.show && (
                    <div style={{...toastStyle, background: toast.type === 'success' ? '#0f172a' : '#dc2626'}}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} color="#10b981" /> : <AlertTriangle size={18} color="white" />}
                        {toast.message}
                    </div>
                )}
            </main>
        </div>
    );
};

// --- STYLES ---
const layoutStyle = { display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const titleContainer = { display: 'flex', alignItems: 'center', gap: '15px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#0f172a' };
const subtitleStyle = { fontSize: '12px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' };
const iconBoxStyle = { background: '#2563eb', padding: '10px', borderRadius: '12px' };
const tableWrapper = { padding: '20px 40px', flex: 1, overflowY: 'auto' };
const tableStyle = { width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' };
const thStyle = { textAlign: 'left', padding: '10px', fontSize: '11px', color: '#64748b', fontWeight: '800', textTransform: 'uppercase' };
const trStyle = { background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' };
const tdStyle = { padding: '15px 10px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', fontSize: '13px' };
const btnDelete = { background: '#fef2f2', border: 'none', color: '#dc2626', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const btnEdit = { background: '#eff6ff', border: 'none', color: '#2563eb', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const badgeStyle = (s) => ({ background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900' });
const confirmToastStyle = { position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '15px 25px', borderRadius: '20px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: '500px', zIndex: 9999, border: '2px solid #e2e8f0' };
const warnIcon = { background: '#f59e0b', padding: '8px', borderRadius: '12px' };
const confirmText = { fontWeight: '800', color: '#0f172a', fontSize: '13px' };
const btnToastConfirm = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const btnToastCancel = { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const btnAdd = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const btnCancel = { ...btnAdd, background: '#fee2e2', color: '#dc2626' };
const formWrapper = { background: 'white', padding: '15px 40px', borderBottom: '1px solid #e2e8f0' };
const inlineForm = { display: 'flex', alignItems: 'flex-end', gap: '20px' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b' };
const inputStyle = { padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '13px', width: '100%' };
const btnSubmit = { background: '#2563eb', color: 'white', border: 'none', padding: '11px 25px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer' };
const toastStyle = { position: 'fixed', top: '20px', right: '20px', padding: '15px 30px', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 9999, fontWeight: '700' };
const centerStyle = { display: 'flex', justifyContent: 'center', padding: '100px' };

export default CycleScolaire;